import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient as supabase } from '../../config/supabase';
import InviteInstallSettings from './InviteInstallSettings';
import Button3D from '../ui/Button3D';
import { useSaveToast } from '../ui/SaveToast';
import NotificationDefaultsGroup from './notificationDefaults/NotificationDefaultsGroup';
import './notificationDefaults/notificationDefaults.css';
import {
    notificationPreferenceChannels,
    buildOverrideMap,
    getEffectiveChannels,
    normalizeNotificationWorkflows,
    shouldShowSeniorChannelNudge,
} from '../../lib/notificationPreferences';
import {
    DEFAULTS_CHANNEL_LABELS,
    DEFAULTS_FILTERS,
    DEFAULTS_GROUPS,
    canManageNotificationDefaults,
    companyLabel,
    loadCompanySwitches,
    matchesFilter,
    matchesSearch,
    notificationGroup,
    possessive,
    resetCompanyDefaults,
    setCompanySwitch,
} from '../../lib/notificationPreferencesDefaults';

import TripChangeSettings from './TripChangeSettings';
import TripFeedbackSettings from './TripFeedbackSettings';
import ClientTermsSettings from './ClientTermsSettings';
import FlightTrackingSettings from './FlightTrackingSettings';
import { loadCurrentCompanyName } from '../../lib/currentCompany';

function orderChannels(workflow, set) {
    return notificationPreferenceChannels(workflow).filter((channel) => set.has(channel));
}

function storedTenantName() {
    try {
        return globalThis.localStorage?.getItem('intelAgentTenantName') || '';
    } catch (storageFailure) {
        return '';
    }
}

// BL-177 Step 2d (section 4, board 04): Admin Center > Notification Defaults.
// Each notification = ONE company-wide Switch + a Checkbox per channel. Every change saves on its own (optimistic,
// green "Saved ✓" on success; red error + the control put back on failure). Only this company's admins and
// platform admins ever see it: anyone else gets nothing rendered (the /admin route also redirects them).
function NotificationPreferencesSettingsView({ orgId, companyName }) {
    const toast = useSaveToast();
    const [access, setAccess] = useState('checking'); // checking | allowed | denied | error
    const [company, setCompany] = useState(() => companyName || storedTenantName());
    const [workflows, setWorkflows] = useState([]);
    // eventType -> { channels, pending: string[], enabled, switchPending }
    const [cards, setCards] = useState({});
    const [criticalEvents, setCriticalEvents] = useState({});
    const [seniorAccountCount, setSeniorAccountCount] = useState(0);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [confirmingReset, setConfirmingReset] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Write queues live outside React state so two flips in the same tick never read stale data.
    // Channels, per event: { server, desired, running, workflow } (screen = server + desired).
    // Company switch, per event: { server, desired (boolean | undefined), running }.
    const queues = useRef({});
    const switchQueues = useRef({});
    const resettingRef = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        start();
        return () => { mounted.current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { if (companyName) setCompany(companyName); }, [companyName]);

    async function start() {
        let allowed = false;
        try {
            allowed = await canManageNotificationDefaults(supabase);
        } catch (accessFailure) {
            if (mounted.current) setAccess('error');
            return;
        }
        if (!mounted.current) return;
        setAccess(allowed ? 'allowed' : 'denied');
        if (!allowed) return;
        loadCompanyName();
        loadSettings();
    }

    async function loadCompanyName() {
        if (companyName || !orgId) return;
        try {
            const name = await loadCurrentCompanyName(supabase, orgId);
            if (mounted.current && name) setCompany(name);
        } catch (nameFailure) {
            // The stored tenant name (or "your company") stays on screen.
        }
    }

    function shownChannels(queue) {
        const set = new Set(queue.server);
        Object.entries(queue.desired).forEach(([channel, enabled]) => {
            if (enabled) set.add(channel); else set.delete(channel);
        });
        return orderChannels(queue.workflow, set);
    }

    function shownSwitch(switchQueue) {
        return typeof switchQueue.desired === 'boolean' ? switchQueue.desired : switchQueue.server;
    }

    function publish(eventType) {
        const queue = queues.current[eventType];
        const switchQueue = switchQueues.current[eventType];
        if (!queue || !switchQueue || !mounted.current) return;
        setCards((current) => ({
            ...current,
            [eventType]: {
                channels: shownChannels(queue),
                pending: Object.keys(queue.desired),
                enabled: shownSwitch(switchQueue),
                switchPending: typeof switchQueue.desired === 'boolean',
            },
        }));
    }

    async function fetchState() {
        let overrideQuery = supabase.from('org_notification_settings').select('event_type, channel, enabled');
        if (orgId) overrideQuery = overrideQuery.eq('org_id', orgId);
        const [{ data: workflowRows, error: workflowError }, { data: overrideRows, error: overrideError }, switches] = await Promise.all([
            supabase
                .from('notification_workflows')
                .select('workflow_key, display_name, event_type, default_channels, is_active, template')
                .eq('is_active', true)
                .order('display_name', { ascending: true }),
            overrideQuery,
            loadCompanySwitches(supabase, orgId).catch((switchFailure) => ({ __error: switchFailure })),
        ]);
        const failure = workflowError || overrideError || switches?.__error;
        if (failure) throw failure;
        return { workflowRows: workflowRows || [], overrideRows: overrideRows || [], switches };
    }

    // Rebuild every row from what the database says (first load, and after "Reset to default").
    function applyState({ workflowRows, overrideRows, switches }) {
        const normalizedWorkflows = normalizeNotificationWorkflows(workflowRows);
        const overrideMap = buildOverrideMap(overrideRows);
        const nextCards = {};
        queues.current = {};
        switchQueues.current = {};
        normalizedWorkflows.forEach((workflow) => {
            const channels = getEffectiveChannels(workflow, overrideMap);
            const enabled = switches[workflow.eventType] !== false;
            queues.current[workflow.eventType] = { workflow, server: channels, desired: {}, running: false };
            switchQueues.current[workflow.eventType] = { server: enabled, desired: undefined, running: false };
            nextCards[workflow.eventType] = { channels, pending: [], enabled, switchPending: false };
        });
        setWorkflows(normalizedWorkflows);
        setCards(nextCards);
        return normalizedWorkflows;
    }

    async function loadSettings() {
        setLoading(true);
        setError(null);
        let state;
        try {
            state = await fetchState();
        } catch (loadFailure) {
            if (!mounted.current) return;
            setError(loadFailure?.message || 'Notification defaults could not be loaded.');
            setLoading(false);
            return;
        }
        if (!mounted.current) return;

        let seniorCount = 0;
        try {
            const { count } = await supabase.from('accounts').select('id', { count: 'exact', head: true }).eq('senior', true);
            seniorCount = count || 0;
        } catch (countFailure) {
            // The senior nudge is advisory only.
        }

        const critical = Object.fromEntries(state.workflowRows
            .filter((row) => row?.template?.severity === 'critical' || row?.template?.factory_locked === true)
            .map((row) => [row.event_type, true]));
        try {
            const { data: severityRows } = await supabase.from('org_notification_severity_settings').select('event_type, severity');
            (severityRows || []).filter((row) => row?.severity === 'critical').forEach((row) => { critical[row.event_type] = true; });
        } catch (severityFailure) {
            // The note is advisory; the factory severity above still applies.
        }
        if (!mounted.current) return;
        setCriticalEvents(critical);
        setSeniorAccountCount(seniorCount);
        applyState(state);
        setLoading(false);
    }

    async function drainChannels(eventType) {
        const queue = queues.current[eventType];
        if (queue.running) return;
        queue.running = true;
        try {
            // One write at a time per row, so the database sees the writes in the order they were made.
            while (true) {
                if (resettingRef.current) { queue.desired = {}; break; }
                const channel = Object.keys(queue.desired).find((key) => queue.desired[key] !== queue.server.includes(key));
                if (!channel) { queue.desired = {}; break; }
                const enabled = queue.desired[channel];
                try {
                    const { data, error: rpcError } = await supabase.rpc('bl177_set_org_notification_channel', {
                        p_event_type: eventType, p_channel: channel, p_enabled: enabled,
                    });
                    if (rpcError) throw rpcError;
                    if (!data || !Array.isArray(data.channels)) throw new Error('The server did not confirm the new setting.');
                    if (queue.desired[channel] === enabled) delete queue.desired[channel];
                    queue.server = data.channels;
                    publish(eventType);
                    toast.saved();
                } catch (failure) {
                    // Put the box back to what the database last confirmed.
                    delete queue.desired[channel];
                    publish(eventType);
                    toast.failed(`${queue.workflow.displayName} ${DEFAULTS_CHANNEL_LABELS[channel] || channel} could not be saved: ${failure?.message || 'please try again'}`);
                }
            }
        } finally {
            queue.running = false;
            publish(eventType);
        }
    }

    async function drainSwitch(eventType) {
        const switchQueue = switchQueues.current[eventType];
        if (switchQueue.running) return;
        switchQueue.running = true;
        const name = queues.current[eventType]?.workflow.displayName || eventType;
        try {
            while (typeof switchQueue.desired === 'boolean' && switchQueue.desired !== switchQueue.server) {
                if (resettingRef.current) break;
                const enabled = switchQueue.desired;
                try {
                    const confirmed = await setCompanySwitch(supabase, { eventType, enabled, orgId });
                    switchQueue.server = confirmed;
                    if (switchQueue.desired === enabled) switchQueue.desired = undefined;
                    publish(eventType);
                    toast.saved();
                } catch (failure) {
                    switchQueue.desired = undefined;
                    publish(eventType);
                    toast.failed(`${name} could not be turned ${enabled ? 'on' : 'off'}: ${failure?.message || 'please try again'}`);
                }
            }
            switchQueue.desired = undefined;
        } finally {
            switchQueue.running = false;
            publish(eventType);
        }
    }

    function toggleChannel(eventType, channel) {
        const queue = queues.current[eventType];
        const switchQueue = switchQueues.current[eventType];
        if (!queue || resettingRef.current || !shownSwitch(switchQueue)) return;
        queue.desired[channel] = !shownChannels(queue).includes(channel);
        publish(eventType);
        drainChannels(eventType);
    }

    function toggleSwitch(eventType) {
        const switchQueue = switchQueues.current[eventType];
        if (!switchQueue || resettingRef.current) return;
        switchQueue.desired = !shownSwitch(switchQueue);
        publish(eventType);
        drainSwitch(eventType);
    }

    async function confirmReset() {
        setConfirmingReset(false);
        resettingRef.current = true;
        setResetting(true);
        try {
            await resetCompanyDefaults(supabase, { orgId });
            const state = await fetchState();
            if (!mounted.current) return;
            applyState(state);
            toast.saved();
        } catch (failure) {
            toast.failed(`Notification defaults could not be reset: ${failure?.message || 'please try again'}`);
        } finally {
            resettingRef.current = false;
            if (mounted.current) setResetting(false);
        }
    }

    const shownCompany = companyLabel(company);
    const rowsByGroup = useMemo(() => {
        const grouped = Object.fromEntries(DEFAULTS_GROUPS.map((group) => [group.key, []]));
        workflows
            .filter((workflow) => matchesFilter(workflow, filter) && matchesSearch(workflow, search))
            .forEach((workflow) => {
                const card = cards[workflow.eventType] || { channels: [], pending: [], enabled: true, switchPending: false };
                const group = notificationGroup(workflow.eventType);
                grouped[group].push({
                    workflow,
                    enabled: card.enabled,
                    switchPending: card.switchPending,
                    channels: card.channels,
                    pendingChannels: card.pending,
                    critical: group === 'staff' && criticalEvents[workflow.eventType] === true,
                    seniorNudge: shouldShowSeniorChannelNudge(workflow, card.channels, seniorAccountCount),
                });
            });
        return grouped;
    }, [workflows, cards, filter, search, criticalEvents, seniorAccountCount]);

    if (access === 'denied') return null;
    if (access === 'error') {
        return (
            <div className="ia-d2">
                <div className="nd-page">
                    <p role="alert" className="nd-alert">We could not check your access to Notification Defaults. Please reload and try again.</p>
                </div>
            </div>
        );
    }
    if (access === 'checking' || loading) {
        return (
            <div className="ia-d2">
                <div className="nd-page"><p className="nd-empty">Loading notification defaults…</p></div>
            </div>
        );
    }

    const visibleCount = DEFAULTS_GROUPS.reduce((total, group) => total + rowsByGroup[group.key].length, 0);

    return (
        <div className="ia-d2" data-testid="notification-defaults">
            <div className="nd-page">
                <div className="nd-head">
                    <div>
                        <h2>Notification Defaults</h2>
                        <p>
                            What {shownCompany} sends, and how. The switch turns a notification on or off for the whole
                            company. The boxes pick its channels. Changes save on their own.
                        </p>
                    </div>
                    <Button3D variant="pill" onClick={() => setConfirmingReset(true)} disabled={resetting || confirmingReset}>
                        {resetting ? 'Resetting…' : 'Reset to default'}
                    </Button3D>
                </div>

                {confirmingReset && (
                    <div role="alertdialog" aria-label="Reset notification defaults" aria-describedby="nd-reset-text" className="nd-confirm">
                        <p id="nd-reset-text" style={{ margin: 0 }}>
                            Put every notification for {shownCompany} back to its default? This turns every notification back on and
                            removes all of {possessive(shownCompany)} channel changes.
                        </p>
                        <div className="nd-confirm-actions">
                            <Button3D variant="go" onClick={confirmReset}>Reset everything</Button3D>
                            <Button3D variant="primary" onClick={() => setConfirmingReset(false)}>Cancel</Button3D>
                        </div>
                    </div>
                )}

                <div className="nd-tools">
                    <div role="group" aria-label="Show notifications for" className="nd-tools">
                        {DEFAULTS_FILTERS.map((option) => (
                            <Button3D
                                key={option.key}
                                variant={filter === option.key ? 'go' : 'primary'}
                                aria-pressed={filter === option.key}
                                onClick={() => setFilter(option.key)}
                            >
                                {option.label}
                            </Button3D>
                        ))}
                    </div>
                    <label className="nd-search">
                        <span className="nd-sr">Search notifications</span>
                        <input
                            type="search"
                            value={search}
                            placeholder="Search notifications"
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </label>
                </div>

                <p className="nd-who">Only {possessive(shownCompany)} admins and platform admins can change these.</p>

                {error && <p role="alert" className="nd-alert">{error}</p>}

                {!error && workflows.length === 0 && <p className="nd-empty">No active notifications are set up yet.</p>}
                {workflows.length > 0 && visibleCount === 0 && <p className="nd-empty">No notifications match.</p>}

                {DEFAULTS_GROUPS.map((group) => (
                    <NotificationDefaultsGroup
                        key={group.key}
                        label={group.label}
                        rows={rowsByGroup[group.key]}
                        company={shownCompany}
                        busy={resetting}
                        onToggleSwitch={toggleSwitch}
                        onToggleChannel={toggleChannel}
                    />
                ))}

                {orgId && (
                    <section className="nd-more" aria-label="More company settings">
                        <h3>More company settings</h3>
                        <InviteInstallSettings orgId={orgId} />
                        <TripFeedbackSettings orgId={orgId} />
                        <TripChangeSettings orgId={orgId} />
                        <p className="text-sm" data-testid="company-words-pointer">The word your clients see for their bookings now lives in <strong>Admin Center › Company Words</strong>, where one setting renames it for your team and your clients.</p>
                        <ClientTermsSettings orgId={orgId} />
                        <FlightTrackingSettings orgId={orgId} />
                    </section>
                )}
            </div>
        </div>
    );
}

export default NotificationPreferencesSettingsView;
